"use strict";

import SessionManager from "session-mgmt";
import cookieToken from "./cookie-token";
import whitelabelUrl from "./whitelabel-url";
import redirectAfterLogin from "./redirect-after-login";
import jwt_decode from "jwt-decode";
import * as Sentry from "@sentry/browser";

function fromCallback() {
  return window.location.href.includes("oauth/callback");
}

let clockOffsetMs = 0;
let loginUrl = CONFIGURATION.authUrl;

if (CONFIGURATION.backupAuthEnabled) {
  loginUrl = `${CONFIGURATION.redirectToUri}/login`;
}

const sessionSDK = new SessionManager({
  postLogoutRedirectURI: whitelabelUrl.get("/login"),
  loginUrl: loginUrl,
  isDevelopment: CONFIGURATION.debugOrTest,
  logger: true,
  onLogout: function () {
    cookieToken.remove();
    window.dispatchEvent(new Event("session:loggedout"));
    redirectAfterLogin.persistUrl();
  },
});

const FronteggPort = {
  add: function (app, clientId, authUrl) {
    const fronteggApp = Frontegg.initialize({
      contextOptions: {
        baseUrl: authUrl,
        clientId: clientId,
        tenantResolver: () => {
          return {
            tenant: whitelabelUrl.setAndRetrieve(),
          };
        },
      },
      authOptions: {
        keepSessionAlive: true,
      },
      hostedLoginBox: true,
    });

    app.ports.fronteggAuthorizePort.subscribe(function () {
      subscribeToAuth();
    });

    if (app.ports.fronteggRefreshSessionPort) {
      app.ports.fronteggRefreshSessionPort.subscribe(function () {
        loginWithRedirect("refreshing session");
      });
    }

    app.ports.fronteggPersistRedirectPort.subscribe(function (url) {
      redirectAfterLogin.persist(url);
    });

    function loginWithRedirect(loginFrom) {
      if (!CONFIGURATION.skipSSOInit && !CONFIGURATION.backupAuthEnabled) {
        fronteggApp.ready(() => {
          try {
            setTimeout(() => {
              if (!cookieToken.load()) {
                Sentry.captureMessage("Retrying login", "debug");
                loginWithRedirect("retry");
              }
            }, 5000);
            console.log("Attempting to login from", loginFrom);
            fronteggApp.loginWithRedirect();
          } catch (e) {
            console.log("Error", e);
          }
        });
      } else {
        if (cookieToken.load()) {
          window.dispatchEvent(new Event("session:updated"));
        }
      }
    }

    function onAccessTokenChange(accessToken) {
      const isNewToken = !cookieToken.load();
      clockOffsetMs = jwt_decode(accessToken).iat * 1000 - Date.now();
      cookieToken.set(accessToken);

      if (isNewToken || fromCallback()) {
        const eventData = {
          detail: {
            accessToken: accessToken,
            redirectToURI: redirectAfterLogin.retrieveUrl(),
          },
        };
        window.dispatchEvent(
          new CustomEvent("session-token:created", eventData),
        );
        redirectAfterLogin.resetUrl();
      } else {
        window.dispatchEvent(new Event("session:updated"));
      }
    }

    function onUnAuthenticated() {
      redirectAfterLogin.persistUrl();
      if (!fromCallback()) {
        loginWithRedirect("unauthenticated");
      }
    }

    function subscribeToAuth() {
      sessionSDK.run({
        app: fronteggApp,
        onExpirationWarning: function () {
          window.dispatchEvent(new Event("session:warning"));
        },
        onUnAuthenticated: onUnAuthenticated,
        onAccessTokenChange: onAccessTokenChange,
      });
    }

    function isTokenExpired(accessToken) {
      let decoded = jwt_decode(accessToken);
      const refreshInAdvanceMs = 5000;
      if (
        Date.now() + clockOffsetMs + refreshInAdvanceMs >=
        decoded.exp * 1000
      ) {
        return true;
      }
      return false;
    }

    function throttle(func, timeFrame) {
      var lastTime = 0;
      return function () {
        var now = Date.now();
        if (now - lastTime >= timeFrame) {
          func();
          lastTime = now;
        }
      };
    }

    document.addEventListener(
      "mousemove",
      throttle(function () {
        if (cookieToken.load() && isTokenExpired(cookieToken.load())) {
          console.log("The session is expired, refreshing manually");
          loginWithRedirect("session expired");
        }
      }, 2000),
    );
  },

  logout: function () {
    if (CONFIGURATION.backupAuthEnabled) {
      cookieToken.remove();
      window.location.href = loginUrl; // PLAT-759 will pass the backup enabled flag so the sdk so doesn't append /oauth/logout
    } else {
      sessionSDK.logout();
    }
  },
};

module.exports = FronteggPort;
